import React from 'react';
import { ButtonProps } from '../../types';

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  disabled = false,
  children,
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2';

  const variantClasses = {
    primary: 'text-white shadow-sm focus:ring-[color:var(--color-brand-primary)]',
    secondary: 'bg-white shadow-sm focus:ring-[color:var(--color-brand-primary)]',
    danger: 'bg-white focus:ring-[color:var(--color-status-danger)]',
    ghost: 'focus:ring-[color:var(--color-brand-primary)]',
    outline: 'bg-white shadow-sm focus:ring-[color:var(--color-brand-primary)]',
  };

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      backgroundColor: 'var(--color-brand-primary)',
    },
    secondary: {
      border: '1px solid var(--color-border-default)',
      color: 'var(--color-text-primary)',
    },
    danger: {
      border: '1px solid var(--color-status-danger)',
      color: 'var(--color-status-danger)',
    },
    ghost: {
      color: 'var(--color-text-secondary)',
    },
    outline: {
      border: '1px solid var(--color-border-default)',
      color: 'var(--color-text-primary)',
    },
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const disabledClasses = disabled || loading ? 'opacity-50 cursor-not-allowed' : '';

  const hoverClass = !disabled && !loading ? {
    primary: 'hover:opacity-90',
    secondary: 'hover:bg-[color:var(--color-surface-sidebar)]',
    danger: 'hover:bg-[#FEF2F2]',
    ghost: 'hover:text-[color:var(--color-text-primary)]',
    outline: 'hover:bg-[color:var(--color-surface-sidebar)]',
  }[variant] || '' : '';

  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${hoverClass} ${className}`;

  return (
    <button
      {...props}
      className={buttonClasses}
      style={variantStyles[variant]}
      disabled={disabled || loading}
    >
      {loading ? (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      ) : icon ? (
        <span className="mr-2 -ml-1">{icon}</span>
      ) : null}
      {children}
    </button>
  );
};

export default Button;
